import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import store from '../Store';
import { Country, City } from '../types/LocationTypes';

interface CountryNode extends Country {
  cities?: CityNode[];
  is_active?: boolean;
}

interface CityNode extends City {
  is_active?: boolean;
}

interface Notification {
  type: 'success' | 'error';
  message: string;
}

const CountriesPage: React.FC = observer(() => {
  const [countries, setCountries] = useState<CountryNode[]>([]);
  const [expandedCountries, setExpandedCountries] = useState<Set<number>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateCityModal, setShowCreateCityModal] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<CountryNode | null>(null);
  const [notification, setNotification] = useState<Notification | null>(null);

  useEffect(() => {
    fetchCountries();
  }, []);

  const fetchCountries = async () => {
    try {
      console.log('CountriesPage: Starting to fetch countries...');
      await store.fetchCountries();
      console.log('CountriesPage: Countries fetched:', store.countries);
      console.log('CountriesPage: Countries length:', store.countries.length);
      console.log('CountriesPage: First country:', store.countries[0]);
      console.log('CountriesPage: Countries type:', typeof store.countries);
      console.log('CountriesPage: Countries is array:', Array.isArray(store.countries));
      
      await store.fetchCities();
      console.log('CountriesPage: Cities fetched:', store.cities);
      console.log('CountriesPage: Cities length:', store.cities.length);
      console.log('CountriesPage: First city:', store.cities[0]);
      
      // Convert store countries to CountryNode format
      const countryNodes: CountryNode[] = store.countries.map((country, index) => {
        console.log(`CountriesPage: Processing country ${index}:`, country);
        
        // Find cities for this country
        const countryCities = store.cities.filter(city => {
          console.log(`CountriesPage: Checking city ${city.id} with country_id ${city.country_id} against country ${country.id}`);
          return city.country_id === country.id;
        });
        
        console.log(`CountriesPage: Found ${countryCities.length} cities for country ${country.id}`);
        
        const countryNode = {
          id: Number.parseInt(country.id, 10),
          name: country.name,
          name_en: country.name, // Use name as name_en
          name_ar: country.name_ar || '',
          name_de: country.name_de || '',
          num_of_providers: 0, // Default value
          is_active: country.is_active,
          cities: countryCities.map(city => ({
            id: Number.parseInt(city.id, 10),
            name: city.name,
            name_en: city.name,
            name_ar: city.name_ar || '',
            name_de: city.name_de || '',
            num_of_providers: 0,
            country_id: Number.parseInt(city.country_id, 10),
            country_name_en: country.name,
            country_name_ar: country.name_ar || '',
            country_name_de: country.name_de || '',
            is_active: city.is_active
          }))
        };
        
        console.log(`CountriesPage: Created country node:`, countryNode);
        return countryNode;
      });
      
      console.log('CountriesPage: Converted countries:', countryNodes);
      setCountries(countryNodes);
    } catch (error) {
      console.error('Error fetching countries:', error);
      showNotification('error', 'فشل في تحميل الدول');
    }
  };

  const showNotification = (type: 'success' | 'error', message: string) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleCreateCountry = async (countryData: any) => {
    try {
      await store.createCountry(countryData);
      setShowCreateModal(false);
      showNotification('success', 'تم إضافة الدولة بنجاح');
      await fetchCountries();
    } catch (error) {
      console.error('Error creating country:', error);
      showNotification('error', 'فشل في إضافة الدولة');
    }
  };

  const handleEditCountry = async (countryData: any) => {
    if (!selectedCountry) return;
    
    try {
      await store.updateCountry(selectedCountry.id.toString(), countryData);
      setShowEditModal(false);
      setSelectedCountry(null);
      showNotification('success', 'تم تحديث الدولة بنجاح');
      await fetchCountries();
    } catch (error) {
      console.error('Error updating country:', error);
      showNotification('error', 'فشل في تحديث الدولة');
    }
  };

  const handleDeleteCountry = async (countryId: number) => {
    if (globalThis.confirm('هل أنت متأكد من حذف هذه الدولة؟ سيتم حذف جميع المدن أيضاً.')) {
      try {
        await store.deleteCountry(countryId.toString());
        showNotification('success', 'تم حذف الدولة بنجاح');
        await fetchCountries();
      } catch (error) {
        console.error('Error deleting country:', error);
        showNotification('error', 'فشل في حذف الدولة');
      }
    }
  };

  const handleToggleExpand = (countryId: number) => {
    const newExpanded = new Set(expandedCountries);
    if (newExpanded.has(countryId)) {
      newExpanded.delete(countryId);
    } else {
      newExpanded.add(countryId);
    }
    setExpandedCountries(newExpanded);
  };

  const handleCreateCity = async (cityData: any) => {
    if (!selectedCountry) return;
    
    try {
      await store.createCity({
        ...cityData,
        country_id: selectedCountry.id
      });
      setShowCreateCityModal(false);
      setSelectedCountry(null);
      showNotification('success', 'تم إضافة المدينة بنجاح');
      await fetchCountries();
    } catch (error) {
      console.error('Error creating city:', error);
      showNotification('error', 'فشل في إضافة المدينة');
    }
  };

  const handleEditCity = (city: CityNode) => {
    // For now, we'll just show a notification that city editing is not implemented yet
    showNotification('error', 'تعديل المدن غير متاح حالياً');
  };

  const handleDeleteCity = async (cityId: number) => {
    if (globalThis.confirm('هل أنت متأكد من حذف هذه المدينة؟')) {
      try {
        await store.deleteCity(cityId.toString());
        showNotification('success', 'تم حذف المدينة بنجاح');
        await fetchCountries();
      } catch (error) {
        console.error('Error deleting city:', error);
        showNotification('error', 'فشل في حذف المدينة');
      }
    }
  };

  const handleToggleCountry = async (countryId: number) => {
    try {
      const country = countries.find(c => c.id === countryId);
      if (country) {
        const newStatus = !(country.is_active ?? true);
        await store.updateCountry(countryId.toString(), { is_active: newStatus });
        showNotification('success', `تم ${newStatus ? 'تفعيل' : 'تعطيل'} الدولة بنجاح`);
        await fetchCountries();
      }
    } catch (error) {
      console.error('Error toggling country:', error);
      showNotification('error', 'فشل في تغيير حالة الدولة');
    }
  };

  const handleToggleCity = async (cityId: number) => {
    try {
      const city = countries.flatMap(c => c.cities || []).find(c => c.id === cityId);
      if (city) {
        const newStatus = !(city.is_active ?? true);
        await store.updateCity(cityId.toString(), { is_active: newStatus });
        showNotification('success', `تم ${newStatus ? 'تفعيل' : 'تعطيل'} المدينة بنجاح`);
        await fetchCountries();
      }
    } catch (error) {
      console.error('Error toggling city:', error);
      showNotification('error', 'فشل في تغيير حالة المدينة');
    }
  };

  const getCountryName = (country: CountryNode, currentLanguage: string): string => {
    switch (currentLanguage) {
      case 'ar':
        return country.name_ar || country.name;
      case 'de':
        return country.name_de || country.name_ar || country.name;
      default:
        if (country.name && country.name !== country.name_ar) {
          return country.name;
        }
        return country.name_ar || country.name;
    }
  };

  const getCityName = (city: CityNode, currentLanguage: string): string => {
    switch (currentLanguage) {
      case 'ar':
        return city.name_ar || city.name;
      case 'de':
        return city.name_de || city.name_ar || city.name;
      default:
        if (city.name && city.name !== city.name_ar) {
          return city.name;
        }
        return city.name_ar || city.name;
    }
  };

  const filteredCountries = countries.filter(country => {
    const matchesSearch = 
      getCountryName(country, 'en').toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCountryName(country, 'ar').toLowerCase().includes(searchTerm.toLowerCase()) ||
      getCountryName(country, 'de').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  console.log('CountriesPage: Rendering with countries:', countries.length);
  
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            إدارة الدول والمدن
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            إدارة الدول والمدن في النظام
          </p>
        </div>

        {/* Search and Actions */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="البحث في الدول والمدن..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            />
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            إضافة دولة جديدة
          </button>
        </div>

        {/* Countries Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredCountries.map((country) => (
            <div
              key={country.id}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
            >
              {/* Country Header */}
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                      {getCountryName(country, 'ar')}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {country.name && country.name !== country.name_ar && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200">
                          🇺🇸 {country.name}
                        </span>
                      )}
                      {country.name_ar && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          🇸🇦 {country.name_ar}
                        </span>
                      )}
                      {country.name_de && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          🇩🇪 {country.name_de}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleToggleCountry(country.id)}
                      className={`p-2 rounded-lg transition-all duration-200 hover:scale-110 ${
                        (country.is_active ?? true)
                          ? 'text-orange-600 hover:text-orange-800 hover:bg-orange-50 dark:text-orange-400 dark:hover:text-orange-300 dark:hover:bg-orange-900/20'
                          : 'text-green-600 hover:text-green-800 hover:bg-green-50 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/20'
                      }`}
                      title={(country.is_active ?? true) ? 'تعطيل الدولة' : 'تفعيل الدولة'}
                    >
                      {(country.is_active ?? true) ? (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      )}
                    </button>
                    <button
                      onClick={() => {
                        setSelectedCountry(country);
                        setShowEditModal(true);
                      }}
                      className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/20 rounded-lg transition-all duration-200"
                      title="تعديل الدولة"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDeleteCountry(country.id)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/20 rounded-lg transition-all duration-200"
                      title="حذف الدولة"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Cities Section */}
                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      المدن ({country.cities?.length || 0})
                    </span>
                    <button
                      onClick={() => handleToggleExpand(country.id)}
                      className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 transition-colors"
                    >
                      {expandedCountries.has(country.id) ? 'إخفاء' : 'عرض'}
                    </button>
                  </div>

                  {expandedCountries.has(country.id) && country.cities && (
                    <div className="space-y-2 mt-3">
                      {country.cities.map((city) => (
                        <div
                          key={city.id}
                          className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 dark:text-white">
                              {getCityName(city, 'ar')}
                            </div>
                            <div className="flex gap-1 mt-1">
                              {city.name && city.name !== city.name_ar && (
                                <span className="text-xs text-gray-500">🇺🇸 {city.name}</span>
                              )}
                              {city.name_ar && (
                                <span className="text-xs text-blue-500">🇸🇦 {city.name_ar}</span>
                              )}
                              {city.name_de && (
                                <span className="text-xs text-green-500">🇩🇪 {city.name_de}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleToggleCity(city.id)}
                              className={`p-1 rounded transition-colors ${
                                (city.is_active ?? true)
                                  ? 'text-orange-600 hover:text-orange-800'
                                  : 'text-green-600 hover:text-green-800'
                              }`}
                              title={(city.is_active ?? true) ? 'تعطيل المدينة' : 'تفعيل المدينة'}
                            >
                              {(city.is_active ?? true) ? (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                              )}
                            </button>
                            <button
                              onClick={() => handleEditCity(city)}
                              className="p-1 text-blue-600 hover:text-blue-800 transition-colors"
                              title="تعديل المدينة"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteCity(city.id)}
                              className="p-1 text-red-600 hover:text-red-800 transition-colors"
                              title="حذف المدينة"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          setSelectedCountry(country);
                          setShowCreateCityModal(true);
                        }}
                        className="mt-3 px-3 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                      >
                        إضافة مدينة جديدة
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Create Country Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-white">إضافة دولة جديدة</h3>
                    <p className="text-blue-100 mt-1">أضف دولة جديدة إلى النظام</p>
                  </div>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                handleCreateCountry({
                  name: formData.get('name'),
                  name_ar: formData.get('name_ar'),
                  name_de: formData.get('name_de'),
                });
              }} className="p-6 space-y-6 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الاسم (إنجليزي) *
                    </label>
                    <input
                      id="name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder="أدخل الاسم بالإنجليزية"
                    />
                  </div>

                  <div>
                    <label htmlFor="name_ar" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الاسم (عربي)
                    </label>
                    <input
                      id="name_ar"
                      name="name_ar"
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder="أدخل الاسم بالعربية"
                    />
                  </div>

                  <div>
                    <label htmlFor="name_de" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الاسم (ألماني)
                    </label>
                    <input
                      id="name_de"
                      name="name_de"
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder="أدخل الاسم بالألمانية"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 space-x-reverse pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-purple-600 border border-transparent rounded-lg hover:from-blue-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 shadow-lg"
                  >
                    إضافة الدولة
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Edit Country Modal */}
        {showEditModal && selectedCountry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-white">تعديل الدولة</h3>
                    <p className="text-green-100 mt-1">تعديل بيانات الدولة المحددة</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedCountry(null);
                    }}
                    className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                handleEditCountry({
                  name: formData.get('name'),
                  name_ar: formData.get('name_ar'),
                  name_de: formData.get('name_de'),
                });
              }} className="p-6 space-y-6 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="edit_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الاسم (إنجليزي) *
                    </label>
                    <input
                      id="edit_name"
                      name="name"
                      type="text"
                      defaultValue={selectedCountry.name}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder="أدخل الاسم بالإنجليزية"
                    />
                  </div>

                  <div>
                    <label htmlFor="edit_name_ar" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الاسم (عربي)
                    </label>
                    <input
                      id="edit_name_ar"
                      name="name_ar"
                      type="text"
                      defaultValue={selectedCountry.name_ar}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder="أدخل الاسم بالعربية"
                    />
                  </div>

                  <div>
                    <label htmlFor="edit_name_de" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الاسم (ألماني)
                    </label>
                    <input
                      id="edit_name_de"
                      name="name_de"
                      type="text"
                      defaultValue={selectedCountry.name_de}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder="أدخل الاسم بالألمانية"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 space-x-reverse pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEditModal(false);
                      setSelectedCountry(null);
                    }}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-teal-600 border border-transparent rounded-lg hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200 shadow-lg"
                  >
                    حفظ التغييرات
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Create City Modal */}
        {showCreateCityModal && selectedCountry && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-white">إضافة مدينة جديدة</h3>
                    <p className="text-green-100 mt-1">إضافة مدينة جديدة لـ {selectedCountry.name}</p>
                  </div>
                  <button
                    onClick={() => {
                      setShowCreateCityModal(false);
                      setSelectedCountry(null);
                    }}
                    className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/20 rounded-lg"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>

              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target as HTMLFormElement);
                handleCreateCity({
                  name: formData.get('name'),
                  name_ar: formData.get('name_ar'),
                  name_de: formData.get('name_de'),
                });
              }} className="p-6 space-y-6 max-h-96 overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="city_name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الاسم (إنجليزي) *
                    </label>
                    <input
                      id="city_name"
                      name="name"
                      type="text"
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder="أدخل الاسم بالإنجليزية"
                    />
                  </div>

                  <div>
                    <label htmlFor="city_name_ar" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الاسم (عربي)
                    </label>
                    <input
                      id="city_name_ar"
                      name="name_ar"
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder="أدخل الاسم بالعربية"
                    />
                  </div>

                  <div>
                    <label htmlFor="city_name_de" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      الاسم (ألماني)
                    </label>
                    <input
                      id="city_name_de"
                      name="name_de"
                      type="text"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white transition-colors"
                      placeholder="أدخل الاسم بالألمانية"
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3 space-x-reverse pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => {
                      setShowCreateCityModal(false);
                      setSelectedCountry(null);
                    }}
                    className="px-6 py-3 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white dark:border-gray-600 dark:hover:bg-gray-600 transition-colors"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-teal-600 border border-transparent rounded-lg hover:from-green-700 hover:to-teal-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-all duration-200 shadow-lg"
                  >
                    إضافة المدينة
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Notification */}
        {notification && (
          <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg transform transition-all duration-300 ${
            notification.type === 'success' 
              ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
              : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
          }`}>
            <div className="flex items-center">
              <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {notification.type === 'success' ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                )}
              </svg>
              {notification.message}
            </div>
          </div>
        )}
      </div>
    </div>
  );
});

export default CountriesPage;
